import { useEffect, useRef, useState } from 'react';

const KEYBOARD_VISIBILITY_THRESHOLD = 100;

const getViewportHeight = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const visualViewport = window.visualViewport;
  if (visualViewport) {
    return visualViewport.height;
  }

  return window.innerHeight;
};

const getIsSmallScreen = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth < 640;
};

export const useViewportHeight = () => {
  const initialHeight = getViewportHeight();
  const [height, setHeight] = useState<number | undefined>(initialHeight);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() => getIsSmallScreen());
  const baselineHeightRef = useRef<number | undefined>(initialHeight);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateViewport = () => {
      const nextHeight = getViewportHeight();

      setHeight(nextHeight);
      if (nextHeight) {
        baselineHeightRef.current = Math.max(baselineHeightRef.current ?? nextHeight, nextHeight);
      }

      if (baselineHeightRef.current && nextHeight) {
        setIsKeyboardVisible(
          baselineHeightRef.current - nextHeight > KEYBOARD_VISIBILITY_THRESHOLD,
        );
      }
    };

    const updateScreenSize = () => setIsSmallScreen(getIsSmallScreen());

    const handleResize = () => {
      updateViewport();
      updateScreenSize();
    };

    const visualViewport = window.visualViewport;

    if (visualViewport) {
      visualViewport.addEventListener('resize', updateViewport);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    updateViewport();
    updateScreenSize();

    return () => {
      if (visualViewport) {
        visualViewport.removeEventListener('resize', updateViewport);
      }

      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return { height, isSmallScreen, isKeyboardVisible };
};
